// Message Input Component
// Textarea with send button and image upload support for chat messages

import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { Send, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { createComponentLogger } from '@/utils/logger';
import {
  validateImageFile,
  extractImageFromClipboard,
  formatFileSize,
  processImageForUpload,
  type ProcessedImage
} from '@/utils/imageProcessing';
import { useToast } from '@/hooks/use-toast';

const log = createComponentLogger('MessageInput', 'ai-chat');

// ============================================================================
// Component Props
// ============================================================================

interface MessageInputProps {
  onSend: (content: string, images?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** Auto-focus the textarea on mount */
  autoFocus?: boolean;
}

interface ImagePreview {
  file: File;
  processedImage: ProcessedImage;
}

// ============================================================================
// Main Component
// ============================================================================

export function MessageInput({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  maxLength = 2000,
  autoFocus = false
}: MessageInputProps) {
  const [content, setContent] = useState('');
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus && textareaRef.current && !disabled) {
      // Use requestAnimationFrame to ensure the element is in the DOM
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [autoFocus, disabled]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const processImage = async (file: File): Promise<ImagePreview | null> => {
    try {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast({
          title: 'Invalid image',
          description: validation.error,
          variant: 'destructive'
        });
        return null;
      }

      // Process image (resize, optimize)
      log.info('Processing image', { filename: file.name, size: file.size });
      const processed = await processImageForUpload(file);

      log.info('Image processed successfully', {
        filename: file.name,
        originalSize: file.size,
        processedSize: processed.size,
        dimensions: processed.dimensions
      });

      return {
        file,
        processedImage: processed
      };
    } catch (error) {
      log.error('Failed to process image', { filename: file.name }, error as Error);
      toast({
        title: 'Failed to process image',
        description: (error as Error).message,
        variant: 'destructive'
      });
      return null;
    }
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
return;
}

    setIsProcessing(true);

    const newPreviews: ImagePreview[] = [];
    for (const file of files) {
      const preview = await processImage(file);
      if (preview) {
        newPreviews.push(preview);
      }
    }

    setImages((prev) => [...prev, ...newPreviews]);
    setIsProcessing(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = extractImageFromClipboard(e.nativeEvent);
    if (!file) {
return;
}

    e.preventDefault();
    log.info('Image pasted from clipboard', { filename: file.name });

    setIsProcessing(true);
    const preview = await processImage(file);
    if (preview) {
      setImages((prev) => [...prev, preview]);
    }
    setIsProcessing(false);
  };

  const handleRemoveImage = (index: number) => {
    log.debug('Removing image', { index });
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    const trimmedContent = content.trim();

    if (!trimmedContent && images.length === 0) {
      log.debug('Attempted to send empty message');
      return;
    }

    if (trimmedContent.length > maxLength) {
      log.warn('Message exceeds max length', { length: trimmedContent.length, maxLength });
      return;
    }

    log.debug('Sending message', {
      length: trimmedContent.length,
      imageCount: images.length
    });

    const imageFiles = images.map((img) => img.file);
    onSend(trimmedContent || 'Attached images', imageFiles);
    setContent('');
    setImages([]);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (value: string) => {
    setContent(value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  const isOverLimit = content.length > maxLength;
  const remainingChars = maxLength - content.length;
  const canSend = (content.trim() || images.length > 0) && !isOverLimit && !isProcessing;

  return (
    <div className="flex flex-col gap-2">
      {/* Image previews */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-xl">
          {images.map((img, index) => (
            <div key={index} className="relative group">
              <img
                src={img.processedImage.dataUrl}
                alt={img.file.name}
                className="h-20 w-20 object-cover rounded-lg border border-border/50 shadow-sm hover:shadow-md hover:shadow-primary/10 transition-all"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute -top-2 -right-2 h-6 w-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded-b truncate">
                {formatFileSize(img.processedImage.size)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Upload images"
        />

        {/* Image upload button */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isProcessing}
          className={cn(
            "h-[60px] w-[60px] shrink-0 rounded-xl transition-all",
            disabled || isProcessing
              ? "opacity-60 cursor-not-allowed"
              : "hover:bg-accent hover:border-primary/30"
          )}
          title={isProcessing ? "Processing image..." : "Upload images"}
        >
          <ImageIcon className="h-5 w-5" />
        </Button>

        {/* Text input */}
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled || isProcessing}
          className="min-h-[60px] max-h-[200px] resize-none rounded-xl"
          rows={2}
        />

        {/* Send button */}
        <Button
          onClick={handleSend}
          disabled={disabled || !canSend}
          size="icon"
          className={cn(
            "h-[60px] w-[60px] shrink-0 rounded-xl transition-all shadow-md",
            canSend && !disabled
              ? "bg-gradient-to-br from-primary to-primary/90 hover:from-primary hover:to-primary/80 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
              : "bg-gradient-to-br from-muted/70 to-muted/50 border-2 border-primary/20 opacity-80 cursor-not-allowed"
          )}
          title={canSend ? "Send message" : "Type a message to send"}
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>

      {/* Status and hints */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>
            Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> to send,{' '}
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Shift+Enter</kbd> for new line
          </span>
          {images.length > 0 && (
            <span className="text-primary font-medium">
              • {images.length} image{images.length > 1 ? 's' : ''} attached
            </span>
          )}
          {isProcessing && (
            <span className="text-primary font-medium animate-pulse">
              • Processing image...
            </span>
          )}
        </div>
        <div className={isOverLimit ? 'text-destructive font-medium' : ''}>
          {remainingChars} characters remaining
        </div>
      </div>
    </div>
  );
}
